<script>
  import { navigate } from 'svelte-routing';
  import { validatePassword, getStrengthColor, getStrengthLabel } from '../utils/passwordValidation';

  let email = '';
  let password = '';
  let confirmPassword = '';
  let showPassword = false;
  let showConfirmPassword = false;
  let error = '';
  let loading = false;
  let success = false;
  let registeredEmail = '';

  // Password validation state
  let passwordValidation = { valid: false, errors: [], strength: 'none', checks: {} };
  let passwordsMatch = true;

  // Reactive validation
  $: {
    if (password) {
      passwordValidation = validatePassword(password);
    } else {
      passwordValidation = { valid: false, errors: [], strength: 'none', checks: {} };
    }
  }

  $: {
    if (confirmPassword) {
      passwordsMatch = password === confirmPassword;
    } else {
      passwordsMatch = true;
    }
  }

  $: formValid = email.trim() !== '' && 
                 passwordValidation.valid && 
                 passwordsMatch && 
                 confirmPassword.trim() !== '';

  function togglePasswordVisibility() {
    showPassword = !showPassword;
  }

  function toggleConfirmPasswordVisibility() {
    showConfirmPassword = !showConfirmPassword;
  }

  async function handleSubmit() {
    if (!formValid) {
      error = 'Please fix the errors below';
      return;
    }

    // Block @topdevit.com email addresses
    const emailDomain = email.toLowerCase().split('@')[1];
    if (emailDomain === 'topdevit.com') {
      error = 'Signup failed. Please try again.';
      return;
    }

    loading = true;
    error = '';

    try {
      const response = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        success = true;
        registeredEmail = data.email || email;
      } else {
        error = data.error || 'Signup failed. Please try again.';
        if (data.details && Array.isArray(data.details)) {
          error += '\n' + data.details.join('\n');
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      error = 'An error occurred. Please try again.';
    } finally {
      loading = false;
    }
  }
</script>

<div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 px-4 py-10">
  <div class="max-w-md w-full bg-white rounded-lg shadow-md overflow-hidden border-t-4 border-orange-500">
    <div class="space-y-8 p-8">
      <div class="text-center">
        <img class="w-3/4 mx-auto h-24 w-auto rounded-md" src="/images/buildzilla-logo-lg.jpg" alt="Buildzilla">
        <h2 class="mt-6 text-2xl font-bold text-gray-900 leading-snug">
          You're 2 minutes away from never doing double entry again.
        </h2>
        <p class="mt-3 text-sm font-semibold text-orange-500">
          30-day free trial. No credit card. Real support from day one.
        </p>
      </div>

    {#if success}
      <!-- Success Message -->
      <div class="mt-8 space-y-6">
        <div class="p-4 bg-green-50 border border-green-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">Check Your Email</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>We've sent a verification link to:</p>
                <p class="font-semibold mt-1">{registeredEmail}</p>
                <p class="mt-2">Please check your inbox and click the verification link to activate your account.</p>
                <p class="mt-2 text-xs text-green-600">The link will expire in 24 hours.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <button
            on:click={() => navigate('/login')}
            class="text-sm text-[#313b55] hover:text-[rgb(158,158,158)] font-medium"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    {:else}
      <!-- Signup Form -->
      <form on:submit|preventDefault={handleSubmit} class="mt-8 space-y-6">
        <!-- Email Field -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700">Email Address</label>
          <input 
            id="email" 
            name="email" 
            type="email" 
            required 
            bind:value={email}
            on:input={(e) => email = e.target.value.toLowerCase()}
            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)]"
            placeholder="you@example.com"
            disabled={loading}
          >
        </div>

        <!-- Password Field -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
          <div class="relative mt-1">
            {#if showPassword}
              <input 
                id="password" 
                name="password" 
                type="text"
                required 
                bind:value={password}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)]"
                placeholder="Enter password"
                disabled={loading}
              >
            {:else}
              <input 
                id="password" 
                name="password" 
                type="password"
                required 
                bind:value={password}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)]"
                placeholder="Enter password"
                disabled={loading}
              >
            {/if}
            <button
              type="button"
              on:click={togglePasswordVisibility}
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              tabindex="-1"
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          <!-- Password Strength Indicator -->
          {#if password}
            <div class="mt-2">
              <div class="flex items-center space-x-2">
                <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    class="h-full transition-all duration-300 {getStrengthColor(passwordValidation.strength)}"
                    style="width: {passwordValidation.strength === 'strong' ? '100%' : passwordValidation.strength === 'good' ? '75%' : passwordValidation.strength === 'fair' ? '50%' : passwordValidation.strength === 'weak' ? '33%' : '20%'}"
                  ></div>
                </div>
                <span class="text-xs font-medium text-gray-600">
                  {getStrengthLabel(passwordValidation.strength)}
                </span>
              </div>
            </div>
          {/if}

          <!-- Password Requirements (shown only after user starts typing) -->
          {#if password}
            <div class="mt-3 space-y-1 text-sm">
              <p class="text-gray-600 font-medium">Password must contain:</p>
              <div class="flex items-center space-x-2 {passwordValidation.checks.length ? 'text-green-600' : 'text-gray-500'}">
                <span>{passwordValidation.checks.length ? '✓' : '○'}</span>
                <span>At least 8 characters</span>
              </div>
              <div class="flex items-center space-x-2 {passwordValidation.checks.uppercase ? 'text-green-600' : 'text-gray-500'}">
                <span>{passwordValidation.checks.uppercase ? '✓' : '○'}</span>
                <span>One uppercase letter</span>
              </div>
              <div class="flex items-center space-x-2 {passwordValidation.checks.special ? 'text-green-600' : 'text-gray-500'}">
                <span>{passwordValidation.checks.special ? '✓' : '○'}</span>
                <span>One special character (!@#$%^&*...)</span>
              </div>
            </div>
          {/if}
        </div>

        <!-- Confirm Password Field -->
        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700">Confirm Password</label>
          <div class="relative mt-1">
            {#if showConfirmPassword}
              <input 
                id="confirmPassword" 
                name="confirmPassword" 
                type="text"
                required 
                bind:value={confirmPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)] {confirmPassword && !passwordsMatch ? 'border-red-500' : ''}"
                placeholder="Confirm password"
                disabled={loading}
              >
            {:else}
              <input 
                id="confirmPassword" 
                name="confirmPassword" 
                type="password"
                required 
                bind:value={confirmPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)] {confirmPassword && !passwordsMatch ? 'border-red-500' : ''}"
                placeholder="Confirm password"
                disabled={loading}
              >
            {/if}
            <button
              type="button"
              on:click={toggleConfirmPasswordVisibility}
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              tabindex="-1"
            >
              {showConfirmPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {#if confirmPassword && !passwordsMatch}
            <p class="mt-1 text-sm text-red-600">Passwords do not match</p>
          {/if}
        </div>

        <!-- Error Message -->
        {#if error}
          <div class="p-3 bg-red-50 border border-red-200 rounded-md">
            <p class="text-sm text-red-800 whitespace-pre-line">{error}</p>
          </div>
        {/if}

        <!-- Submit Button (full-colour, validation handled on submit) -->
        <div>
          <button
            type="submit"
            disabled={loading}
            class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
          >
            {#if loading}
              <span class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            {:else}
              Create My Account
            {/if}
          </button>

          <!-- Social proof / trust line -->
          <p class="mt-3 text-center text-xs text-gray-500">
            Joining builders across NZ, AU and the US. 30 days free, no credit card needed.
          </p>
        </div>

        <!-- Back to Login Link -->
        <div class="text-center">
          <span class="text-sm text-gray-600">Already have an account? </span>
          <button
            type="button"
            on:click={() => navigate('/login')}
            class="text-sm text-[#313b55] hover:text-[rgb(158,158,158)] font-medium"
          >
            Sign in
          </button>
        </div>

        <div class="text-center text-sm text-gray-500 mt-4">
          © 2026 All rights reserved
        </div>
      </form>
    {/if}
    </div>
  </div>
</div>

